import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import AdminImportPage from '../shared/adminImportPage.component';
import { adminPageNames, adminPageTitles } from '../shared/adminPageNames';

import AddTaxBookingEntityModal from './addTaxBookingEntityModal.component';
import DeleteTaxBookingEntityModal from './deleteTaxBookingEntityModal.component';
import { RefreshTaxBookingEntitiesNavigationButton } from './refreshTaxBookingEntitiesButton.component';

import TaxBookingEntitiesAdminColumnRenderer from './taxBookingEntitiesAdminColumnRenderer.component';
import columnDefinitions from '../../shared/columnDefinitions/taxBookingEntities';
import { importValidationColumnDefinition } from '../../shared/columnDefinitions';
import { downloadTaxBookEntitiesXlsxFile } from './downloadXlsx';

import {
  sharedActions,
} from './store/actions';
import { withPermissions, permissions } from '../../shared/authorization';
import getPermissionsFromTree from '../../shared/authorization/getPermissionsFromTree.helper';
import permissionPaths from '../../shared/authorization/permissionPaths';
import permissionTypes from '../../shared/authorization/permissionTypes';
import { fromSearchString } from '../../shared/urlUtils';
import { userSelector } from '../../shared/store/selectors';
import { setColumnDefinitionsField } from '../../utils/columnDefinitionUtils';
import { sortBySeverity } from '../../shared/sortBySeverity';

const columnDefinitionsWithDefaultSorting = setColumnDefinitionsField([
  { fieldName: 'sort', value: 'desc', applyTo: 'severity' },
  { fieldName: 'comparator', value: (a, b) => sortBySeverity({ severity: a }, { severity: b }), applyTo: 'severity' },
  { fieldName: 'sort', value: null, applyTo: 'importValidation' },
])([
  { ...importValidationColumnDefinition, field: 'importValidation' },
  ...columnDefinitions,
]);

const TaxBookingEntitiesPage = ({
  canModifyInSelectedPeriod,
}) => {
  const user = useSelector(userSelector);
  const { search } = useLocation();

  const initialFilters = useMemo(() => ({
    companyCode: fromSearchString(search, true)?.companyCode,
    tbeCompanyCode: fromSearchString(search, true)?.tbeCompanyCode,
  }), [search]);

  const canUserCreatePermission = getPermissionsFromTree(
    user.permissions, permissionPaths.TAX_BOOKING_ENTITIES
  )[permissionTypes.CREATE];

  return (
    <>
      <AdminImportPage
        pageName={adminPageNames.TAX_BOOKING_ENTITIES}
        canModify={canModifyInSelectedPeriod}
        AddEditEntityModal={AddTaxBookingEntityModal}
        RemoveEntityModal={DeleteTaxBookingEntityModal}
        adminColumnRenderer={TaxBookingEntitiesAdminColumnRenderer}
        addEntityMessage="Add Tax Booking Entity"
        columnDefs={columnDefinitionsWithDefaultSorting}
        actions={sharedActions}
        comparatorConfig={{
          idField: 'companyCode',
        }}
        pageTitle={adminPageTitles.TAX_BOOKING_ENTITIES}
        disableAddingEntity={!canUserCreatePermission}
        additionalHamburgerMenuItems={
          [
            <RefreshTaxBookingEntitiesNavigationButton
              key="RefreshTaxBookingEntitiesNavigationButton"
            />,
          ]
        }
        exportToExcel={downloadTaxBookEntitiesXlsxFile}
        initialFilters={initialFilters}
      />
      <ReactTooltip place="right" effect="float" />
    </>
  );
};


TaxBookingEntitiesPage.propTypes = {
  canModifyInSelectedPeriod: PropTypes.bool.isRequired,
};

export default withPermissions(permissions.TAX_BOOKING_ENTITIES)(TaxBookingEntitiesPage);
